import { useEffect, useState, useMemo, Fragment } from "react";
import api from "@/lib/api";
import { PageHeader, SectionLabel } from "@/components/Primitives";
import { Loader2, Search, ChevronRight } from "lucide-react";

const INTEGRATIONS = ["audience_source", "campaign_push", "report_sync"];
const LABEL = {
  audience_source: "Audience Source",
  campaign_push: "Campaign Push",
  report_sync: "Report Sync",
};

export default function IntegrationLogs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [successFilter, setSuccessFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/logs/integrations"); setItems(data); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    return items.filter(it => {
      if (filter !== "all" && it.integration !== filter) return false;
      if (successFilter === "success" && !it.success) return false;
      if (successFilter === "failure" && it.success) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        const blob = JSON.stringify(it).toLowerCase();
        return blob.includes(s);
      }
      return true;
    });
  }, [items, filter, successFilter, search]);

  return (
    <div className="px-8 md:px-12 py-10 max-w-[1500px]">
      <PageHeader
        eyebrow="Module / 07"
        title="Integration Logs"
        description="Every outbound call to the audience source, campaign push, and reporting platforms — with payload, response, status, and latency."
        testid="logs-header"
      />

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888]" />
          <input
            data-testid="logs-search-input"
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search payload, endpoint, error…"
            className="w-full bg-white border border-[#E0E0D8] rounded-none pl-9 pr-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
          />
        </div>
        <select data-testid="logs-integration-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none">
          <option value="all">All integrations</option>
          {INTEGRATIONS.map(i => <option key={i} value={i}>{LABEL[i]}</option>)}
        </select>
        <select data-testid="logs-status-filter" value={successFilter} onChange={(e) => setSuccessFilter(e.target.value)}
          className="bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none">
          <option value="all">All statuses</option>
          <option value="success">Success only</option>
          <option value="failure">Failures only</option>
        </select>
      </div>

      {loading ? (
        <p className="text-sm text-[#555] flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading logs…</p>
      ) : (
        <div className="border border-[#E0E0D8] bg-white overflow-x-auto" data-testid="logs-table">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-[#0A0A0A]">
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">When</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Integration</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Endpoint</th>
                <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Status</th>
                <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Latency</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(it => (
                <Fragment key={it.id}>
                  <tr className="border-b border-[#E0E0D8] hover:bg-[#F4F4F0] cursor-pointer"
                    data-testid={`log-row-${it.id}`}
                    onClick={() => setOpen(open === it.id ? null : it.id)}>
                    <td className="py-3 px-4 font-mono text-[11px] text-[#555]">{new Date(it.created_at).toLocaleString()}</td>
                    <td className="py-3 px-4 text-xs font-mono uppercase tracking-wider">{LABEL[it.integration]}</td>
                    <td className="py-3 px-4 font-mono text-xs">{it.endpoint}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${
                        it.success ? "text-[#00994D] border-[#00994D]/30 bg-[#00994D]/10" :
                                     "text-[#D91919] border-[#D91919]/30 bg-[#D91919]/10"
                      }`}>{it.success ? "OK · 200" : `FAIL · ${it.status_code}`}</span>
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-xs">{it.latency_ms} ms</td>
                    <td className="py-3 px-4 text-right">
                      <ChevronRight size={14} className={`transition-transform ${open === it.id ? "rotate-90" : ""} text-[#888]`}/>
                    </td>
                  </tr>
                  {open === it.id && (
                    <tr className="bg-[#F4F4F0]">
                      <td colSpan={6} className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <SectionLabel>Request</SectionLabel>
                            <pre className="bg-[#0A0A0A] text-[#E0E0D8] font-mono p-4 text-[11px] overflow-x-auto max-h-[260px]">
{JSON.stringify(it.request_payload, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <SectionLabel>Response</SectionLabel>
                            <pre className="bg-[#0A0A0A] text-[#E0E0D8] font-mono p-4 text-[11px] overflow-x-auto max-h-[260px]">
{JSON.stringify(it.response_payload || { error: it.error }, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-12 text-center text-sm text-[#888] font-mono">No matching logs.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
